/******* Angular Resourse *******/
import { Component, ElementRef, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

/******* Core Resourse *******/
import { CoreConfigService } from '@core/services/config.service';
import { UtilsService } from '@core/services/utils.service';

/******* Plug-In Resourse *******/
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/******* API Resourse *******/
import { Users } from '@api';

/******* Environments Resourse *******/
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {

    // Public
    public coreConfig: any;
    public forgotPasswordForm: UntypedFormGroup;
    public submitted: boolean = false;
    public loading: boolean = false;

    // Private
    private _unsubscribeAll$: Subject<any> = new Subject();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _elementRef: ElementRef,
        private _users: Users,
        private _coreConfigService: CoreConfigService,
        private _utilsService: UtilsService
    ,
                private _translate: TranslateService) {
        this.buildForgotPasswordForm();
        this.configureLayout();
    }

    /**
     * Accessors
     */
    get f() {
        return this.forgotPasswordForm.controls;
    }

    /**
     * Get email value from form
     */
    get email(): string {
        return this.forgotPasswordForm.get('email')?.value || '';
    }

    /**
     * Lifecycle Hooks
     */
    ngOnInit(): void {
        this._coreConfigService.config
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe(config => this.coreConfig = config);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll$.next(null);
        this._unsubscribeAll$.complete();
    }

    /**
     * Public Methods
     */

    /**
     * Submit the form
     */
    public async onSubmit(): Promise<void> {
        this.submitted = true;

        if (this.forgotPasswordForm.invalid) {
            this._utilsService.scrollToInvalid(this._elementRef);
            return;
        }

        const isConfirmed = await this.confirmPasswordReset();

        if (!isConfirmed) return;

        this.sendResetLink();
    }

    /**
     * Private Methods
     */

    /**
     * Build Forgot Password Form
     */
    private buildForgotPasswordForm(): void {
        this.forgotPasswordForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]]
        });
    }

    /**
     * Configure Layout
     */
    private configureLayout(): void {
        this._coreConfigService.setConfig({
            layout: {
                footer: { hidden: true },
                menu: { hidden: true },
                navbar: { hidden: true },
                buyNow: false,
                customizer: false,
                scrollTop: false,
                enableLocalStorage: false,
                skin: "dark",
                type: "vertical"
            }
        });
    }

    /**
     * Confirm Password Reset Dialog
     */
    private async confirmPasswordReset(): Promise<boolean> {
        const { isConfirmed } = await this._utilsService.swalMixin.fire({
            width: "600px",
            title: this._translate.instant('AUTH.SEND_RESET_LINK'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: this._translate.instant('COMMON.CONFIRM'),
            html: `<p>${this._translate.instant('AUTH.CONFIRM_RESET_PASSWORD', {email: this.email})}</p>`,
            showClass: { popup: 'animate__animated animate__bounceIn' },
            customClass: {
                input: 'remove-from-role mt-2 mb-2',
                title: 'mt-2 mb-2',
                content: 'role-text',
                confirmButton: 'btn btn-danger',
                cancelButton: 'btn btn-secondary'
            }
        });

        return isConfirmed;
    }

    /**
     * Send Reset Link
     */
    private sendResetLink(): void {
        this.loading = true;

        const inputData = {
            clientSecret: environment.CLIENT_SECRET,
            clientId: environment.CLIENT_ID,
            email: this.email
        };

        this._users.forgotPassword(inputData).subscribe({
            next: (results) => this.handleResetLinkSuccess(results),
            error: (error) => this.handleResetLinkError(error)
        });
    }

    /**
     * Handle Reset Link Success
     */
    private handleResetLinkSuccess(results: any): void {
        this.loading = false;
        this._utilsService.alert("success", this._translate.instant('ALERTS.SUCCESS'), results.data);
        this._utilsService.clearCookie();
        this._router.navigate(['/login']);
    }

    /**
     * Handle Reset Link Error
     */
    private handleResetLinkError(error: any): void {
        this.loading = false;
        this._utilsService.alert("error", this._translate.instant('ALERTS.FAIL_TO_UPDATE_PASSWORD'), error);
    }
}
